import React, { useState } from 'react';
import { Bell, Check } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '@/services/api';

const NotificationBell: React.FC = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await notificationApi.list()).data,
    refetchInterval: 30000, // light polling
  });

  const readAll = useMutation({
    mutationFn: () => notificationApi.readAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 rounded-xl glass-light border border-white/8 hover:border-white/15 flex items-center justify-center text-slate-300 hover:text-white transition-all"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center font-bold">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto gradient-border rounded-2xl bg-surface z-50 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="text-white font-bold text-sm">Notifications</span>
              {unread > 0 && (
                <button onClick={() => readAll.mutate()} className="text-violet-400 hover:text-violet-300 text-xs flex items-center gap-1">
                  <Check size={12} /> Mark all read
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">No notifications.</div>
            ) : (
              items.map(n => (
                <div key={n.id} className={`px-4 py-3 border-b border-white/5 ${n.readAt ? '' : 'bg-violet-500/5'}`}>
                  <div className="flex items-start gap-2">
                    {!n.readAt && <span className="w-2 h-2 rounded-full bg-violet-400 mt-1.5 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-white text-sm font-semibold">{n.title}</div>
                      <div className="text-slate-400 text-xs">{n.body}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
